import type { UsageMode } from "./usage-mode";

import type { AppliedProductPromotion } from "./product-promotion";
export interface TimeRange {
  openTime: string; // "HH:mm" e.g. "09:00"
  closeTime: string; // "HH:mm" e.g. "18:00"
}

export interface DaySchedule {
  isOpen: boolean;
  ranges: TimeRange[]; // At least 1 range when isOpen=true
}

export interface ClosurePeriod {
  id: string;
  name: string;
  startDate: string; // ISO date string
  endDate: string; // ISO date string
  startTime?: string; // "HH:mm" for partial-day closures
  endTime?: string; // "HH:mm" for partial-day closures
  reason?: string;
}

export interface BusinessHours {
  enabled: boolean;
  schedule: {
    0: DaySchedule; // Sunday
    1: DaySchedule; // Monday
    2: DaySchedule; // Tuesday
    3: DaySchedule; // Wednesday
    4: DaySchedule; // Thursday
    5: DaySchedule; // Friday
    6: DaySchedule; // Saturday
  };
  closurePeriods: ClosurePeriod[];
}

// ============================================================================
// Tax Settings
// ============================================================================

export interface TaxSettings {
  enabled: boolean; // Activer les taxes
  defaultRate: number; // Taux par défaut (ex: 20 pour 20%)
  displayMode: "inclusive" | "exclusive"; // TTC (inclusive) ou HT (exclusive)
  taxLabel?: string; // Label personnalisé (défaut: "TVA")
  taxNumber?: string; // N° TVA de la boutique
}

export interface ProductTaxSettings {
  inheritFromStore: boolean; // true = utiliser taux boutique
  customRate?: number; // Taux personnalisé si false
}

export type ProductImageVersionKind = "original" | "cropped" | "ai-enhanced" | "background-removed";

export interface ProductImageVersion {
  id: string;
  url: string;
  kind: ProductImageVersionKind;
  createdAt?: string;
}

/** All non-destructive versions that belong to one logical product photo. */
export interface ProductImageHistory {
  id: string;
  versions: ProductImageVersion[];
}

// ============================================================================
// Product Booking Attributes (SKU tracking advanced mode)
// ============================================================================

export interface BookingAttributeAxis {
  /**
   * Stable key used for persistence and matching.
   * This key should be immutable once created.
   */
  key: string;
  /** Human-readable label shown in UI */
  label: string;
  /** Display and canonical ordering */
  position: number;
}

export type UnitAttributes = Record<string, string>;

export interface ResolvedCombination {
  combinationKey: string;
  selectedAttributes: UnitAttributes;
}

// ============================================================================
// Billing Address
// ============================================================================

export interface BillingAddress {
  useSameAsStore: boolean; // true = use store address, false = use custom billing address
  address?: string;
  city?: string;
  postalCode?: string;
  country?: string;
}

// ============================================================================
// Delivery Settings
// ============================================================================

/**
 * Delivery mode determines how delivery is offered to customers:
 * - 'optional': Customer chooses between pickup and delivery (default)
 * - 'required': Outbound delivery is mandatory, return remains selectable
 * - 'included': Outbound delivery is mandatory and free (included in price)
 */
export type DeliveryMode = "optional" | "required" | "included";

/**
 * Method for a single delivery leg (outbound or return).
 * - 'store': Equipment is handled at the store (pickup/return in person)
 * - 'address': Equipment is delivered to/collected from a custom address
 */
export type LegMethod = "store" | "address";

export interface DeliverySettings {
  /** Whether delivery is enabled for this store */
  enabled: boolean;
  /** Whether customers can choose configured pickup/return locations */
  multiLocationEnabled?: boolean;
  /** How delivery is offered to customers */
  mode: DeliveryMode;
  /** Price per kilometer in store currency */
  pricePerKm: number;
  /** Minimum delivery fee per leg, regardless of distance */
  minimumFee: number;
  /** Maximum delivery distance in km, null = unlimited */
  maximumDistance: number | null;
  /** Order subtotal above which delivery is free, null = no free delivery */
  freeDeliveryThreshold: number | null;
  /** Minimum order subtotal required to offer address delivery, null = always available */
  minimumOrderAmountForDelivery?: number | null;
}

export interface ReservationLocationSnapshot {
  type: "primary" | "additional";
  name: string;
  address: string | null;
  city: string | null;
  postalCode: string | null;
  country: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

// ============================================================================
// Inspection Settings (Etat des lieux)
// ============================================================================

/**
 * Inspection mode determines when inspections are prompted:
 * - 'optional': Staff can skip inspections
 * - 'recommended': Reminder shown but can be skipped
 * - 'required': Cannot change status without completing inspection
 */
export type InspectionMode = "optional" | "recommended" | "required";

export interface InspectionSettings {
  /** Whether inspection feature is enabled */
  enabled: boolean;
  /** How inspections are enforced */
  mode: InspectionMode;
  /** Require customer signature on inspections */
  requireCustomerSignature: boolean;
  /** Auto-generate PDF after inspection completion */
  autoGeneratePdf: boolean;
  /** Maximum photos per inspection item */
  maxPhotosPerItem: number;
}

/**
 * Default inspection settings for new stores
 */
export const DEFAULT_INSPECTION_SETTINGS: InspectionSettings = {
  enabled: false,
  mode: "optional",
  requireCustomerSignature: true,
  autoGeneratePdf: true,
  maxPhotosPerItem: 10,
};

// ============================================================================
// Integrations Settings
// ============================================================================

export type TulipPublicMode = "required" | "optional" | "no_public";

export interface IntegrationStateSettings {
  enabled?: boolean;
}

export type IntegrationStates = Record<string, IntegrationStateSettings>;

export interface IntegrationData {
  states?: IntegrationStates;
}

// ============================================================================
// Contact Settings (storefront contact page)
// ============================================================================

/** Whether the storefront contact form asks the visitor for a phone number. */
export type StoreContactPhoneField = "hidden" | "optional" | "required";

/**
 * Shape of the contact page. `full` lists every channel beside the form;
 * `message` puts the form first with the email as a footnote; `single`
 * makes one channel the page's one big action.
 */
export type StoreContactLayout = "full" | "message" | "single";

/** The channel a `single` contact page is built around. */
export type StoreContactPrimaryChannel = "phone" | "whatsapp" | "email";

/**
 * Which ways of reaching the store its contact page offers. Phone, SMS and
 * email read the store's own contact fields; a channel stays hidden while
 * the field it needs is empty, whatever its toggle says.
 */
export interface StoreContactSettings {
  /** Page shape; the channel toggles below still decide what shows. */
  layout: StoreContactLayout;
  /** Channel the `single` layout is built around. */
  primaryChannel: StoreContactPrimaryChannel;
  /** Offer a call to the store phone. */
  phone: boolean;
  /** Offer an SMS to the store phone. */
  sms: boolean;
  /** Offer a WhatsApp conversation. */
  whatsapp: boolean;
  /** Number behind the WhatsApp link; the store phone when null. */
  whatsappNumber: string | null;
  /** Show the store email. */
  email: boolean;
  /** Show the contact form, whose messages are forwarded by email. */
  form: boolean;
  /** Where form messages go; the store email when null. */
  formRecipientEmail: string | null;
  /** Whether the form asks the visitor for a phone number. */
  formPhoneField: StoreContactPhoneField;
  /** Short text at the top of the contact page (plain text). */
  intro: string | null;
}

/** What a store offers before it has visited the contact settings. */
export const DEFAULT_STORE_CONTACT_SETTINGS: StoreContactSettings = {
  layout: "full",
  primaryChannel: "phone",
  phone: true,
  sms: false,
  whatsapp: false,
  whatsappNumber: null,
  email: true,
  form: true,
  formRecipientEmail: null,
  formPhoneField: "optional",
  intro: null,
};

// ============================================================================
// Store Settings
// ============================================================================

/** What the store owner set up for search engines. */
export interface StoreSeoSettings {
  /**
   * Token of the Google Search Console HTML-tag verification, rendered as
   * `<meta name="google-site-verification">` on every storefront page.
   */
  googleSiteVerification?: string | null;
}

export interface StoreSettings {
  automaticExtensions?: boolean;
  maxExtensionDays?: number | null;
  reservationMode: "payment" | "request";
  /** Minimum rental duration in minutes. 0 = no restriction. Default: 60. */
  minRentalMinutes?: number;
  /** Maximum rental duration in minutes. null = no limit. */
  maxRentalMinutes?: number | null;
  /** Minimum notice before start in minutes. Default: 1440 (24h). */
  advanceNoticeMinutes: number;
  /** Operational buffer after each return before stock is available again. Default: 0. */
  turnoverBufferMinutes?: number;
  requireCustomerAddress?: boolean;
  /**
   * Controls whether pending (unanswered) reservation requests block availability.
   * Only applies when reservationMode is 'request'.
   * - true (default): Pending requests immediately block product availability
   * - false: Only confirmed reservations block availability
   */
  pendingBlocksAvailability?: boolean;
  /**
   * Percentage of rental amount to collect immediately during online checkout.
   * Only applies when reservationMode is 'payment'.
   * - 100 (default): Full payment required upfront
   * - 10-99: Partial payment (deposit), remainder due at pickup
   */
  onlinePaymentDepositPercentage?: number;
  businessHours?: BusinessHours;
  /**
   * Fallback language of the storefront (ISO 639-1, e.g. 'fr'): served
   * when the visitor's browser asks for no supported language. Crawlers
   * carry no preference, so this is the language search engines index the
   * store in. Null or absent: the platform default.
   */
  locale?: string | null;
  /** Search engine settings, see `StoreSeoSettings`. */
  seo?: StoreSeoSettings;
  country?: string; // ISO 3166-1 alpha-2 (e.g., 'FR', 'BE', 'CH')
  timezone?: string; // IANA timezone (e.g., 'Europe/Paris')
  currency?: string; // ISO 4217 currency code (e.g., 'EUR', 'USD', 'GBP')
  tax?: TaxSettings; // Configuration des taxes
  billingAddress?: BillingAddress; // Separate billing address for contracts
  delivery?: DeliverySettings; // Delivery configuration
  inspection?: InspectionSettings; // Inventory inspection (etat des lieux)
  integrationData?: IntegrationData;
  /** Storefront contact page: channels offered and form forwarding. */
  contact?: StoreContactSettings;
  /** Public profiles shown in the storefront footer. */
  social?: StoreSocialLinks;
  /** Free text at the bottom of the storefront footer (SIRET, accreditation, affiliation). */
  footerNote?: string | null;
  /**
   * How the renter uses Reserlo: see `UsageMode`. Absent means `storefront`,
   * so every store that existed before this setting keeps its public shop.
   */
  usageMode?: UsageMode;
  /**
   * The renter's own website, used by the `external-website` mode for the
   * back link in the storefront. Absolute http(s) URL.
   */
  externalWebsiteUrl?: string | null;
}

// ============================================================================
// Online store (public site) content settings
// ============================================================================

export const STORE_SOCIAL_NETWORKS = [
  "instagram",
  "facebook",
  "tiktok",
  "youtube",
  "linkedin",
  "x",
  "website",
] as const;

export type StoreSocialNetwork = (typeof STORE_SOCIAL_NETWORKS)[number];

/** Public profiles linked from the storefront footer and the LocalBusiness `sameAs`. Full URLs. */
export type StoreSocialLinks = Partial<Record<StoreSocialNetwork, string | null>>;

/** One line above the storefront header: a closure, a promo code, an event. */
export interface StoreAnnouncement {
  enabled: boolean;
  text: string;
  /** Where the line links to; null for plain text. */
  href: string | null;
}

/** Which optional blocks the home page shows; every block is on until the store turns it off. */
export interface StoreHomeSections {
  /** Address, contact rows and the map, under the inventory. */
  map: boolean;
  /** Google reviews section and the rating pill in the hero. */
  reviews: boolean;
  /** The reassurance line under the period search. */
  reassurance: boolean;
}

export const DEFAULT_STORE_HOME_SECTIONS: StoreHomeSections = {
  map: true,
  reviews: true,
  reassurance: true,
};

export interface StoreTheme {
  mode: "light" | "dark";
  primaryColor: string;
  heroImages?: string[];
  /** Home hero layout. 'cover' (default) = the text over full-width photos; 'split' = the text beside a framed photo. Without photos both render the same centred band. */
  heroLayout?: "cover" | "split";
  /** Where the hero text sits. 'center' by default; ignored without photos (always centred). */
  heroAlign?: "start" | "center" | "end";
  /** Vertical position of the hero text: top, middle or bottom. 'end' (bottom) by default; ignored without photos. */
  heroVerticalAlign?: "start" | "center" | "end";
  /** How the storefront catalog is browsed. 'products' (default) = flat product grid. 'categories' = category cards first, then products. */
  catalogBrowseMode?: "products" | "categories";
  maxDiscountPercent?: number | null;
  /** The line above the header; absent or disabled means no bar. */
  announcement?: StoreAnnouncement;
  /** Optional home blocks; absent means every block shows. */
  homeSections?: StoreHomeSections;
  /** Dedicated social share image (og:image); the first hero photo when null. */
  shareImageUrl?: string | null;
  /** Show a call button in the storefront header (needs a store phone). */
  headerPhone?: boolean;
}

/**
 * Unified notification template interface.
 * Used for both email and SMS customization.
 * @deprecated Use NotificationTemplate instead for new code
 */
export interface EmailCustomContent {
  subject?: string;
  greeting?: string;
  message?: string;
  signature?: string;
}

/**
 * Modern unified notification template.
 * Supports both email and SMS with consistent field names.
 */
export interface NotificationTemplate {
  /** Email subject line (supports variables: {name}, {number}, {storeName}) */
  subject?: string;
  /** Custom email message body (supports variables) */
  emailMessage?: string;
  /** Custom SMS message (supports variables, max 160 chars recommended) */
  smsMessage?: string;
}

/**
 * Convert legacy EmailCustomContent to NotificationTemplate
 */
export function toNotificationTemplate(
  legacy: EmailCustomContent | undefined,
): NotificationTemplate | undefined {
  if (!legacy) return undefined;
  return {
    subject: legacy.subject,
    emailMessage: legacy.message,
  };
}

/**
 * Convert NotificationTemplate to legacy EmailCustomContent (for backward compat)
 */
export function toLegacyEmailContent(
  template: NotificationTemplate | undefined,
): EmailCustomContent | undefined {
  if (!template) return undefined;
  return {
    subject: template.subject,
    message: template.emailMessage,
  };
}

export interface EmailSettings {
  // Toggle settings
  confirmationEnabled: boolean;
  reminderPickupEnabled: boolean;
  reminderReturnEnabled: boolean;
  replyToEmail: string | null;

  // Custom email content
  defaultSignature?: string;
  confirmationContent?: EmailCustomContent;
  rejectionContent?: EmailCustomContent;
  pickupReminderContent?: EmailCustomContent;
  returnReminderContent?: EmailCustomContent;
  requestAcceptedContent?: EmailCustomContent;
}

export interface ProductSnapshot {
  name: string;
  description: string | null;
  images: string[];
  combinationKey?: string | null;
  selectedAttributes?: UnitAttributes | null;
}

// ============================================================================
// Promo Code Types
// ============================================================================

export type PromoCodeType = "percentage" | "fixed";

export interface PromoCodeSnapshot {
  code: string;
  type: PromoCodeType;
  value: number;
}

// ============================================================================
// Pricing Types
// ============================================================================

export type PricingMode = "hour" | "day" | "week";
export type PricingKind = "duration" | "fixed";
export type StockKind = "returnable" | "consumable" | "untracked";

export interface PricingTier {
  id: string;
  minDuration: number | null; // Minimum units to trigger this tier
  discountPercent: number | null; // Discount percentage (0-99)
  displayOrder: number;
}

export interface Rate {
  id: string;
  price: number;
  period: number; // Period in minutes
  displayOrder: number;
}

export interface PricingBreakdown {
  promotion?: AppliedProductPromotion | null;
  basePrice: number;
  effectivePrice: number;
  duration: number;
  pricingMode: PricingMode;
  pricingKind?: PricingKind;
  discountPercent: number | null;
  discountAmount: number;
  tierApplied: string | null; // Human-readable tier label
  // V2 rate-based pricing fields
  durationMinutes?: number;
  appliedPeriods?: number;
  appliedRates?: Array<{ period: number; price: number; quantity: number }>;
  optimizerVersion?: string;
  // Tax fields
  taxRate: number | null;
  taxAmount: number | null;
  subtotalExclTax: number | null;
  subtotalInclTax: number | null;
  // Manual price override fields
  isManualOverride?: boolean;
  originalPrice?: number; // Price before manual override
  // Seasonal pricing breakdown
  seasonalSegments?: Array<{
    seasonalPricingId: string | null;
    seasonalPricingName: string | null;
    startDate: string;
    endDate: string;
    subtotal: number;
  }>;
}

// ============================================================================
// Billing mode (subscription vs pay-as-you-go)
// ============================================================================

/**
 * How a store is billed by the platform.
 * - `subscription`: fixed monthly plan (Start/Pro/Ultra) with usage caps.
 * - `pay_as_you_go`: billed per rental ("location"), no caps.
 */
export type BillingMode = "subscription" | "pay_as_you_go";

/**
 * A single graduated pricing band. `upToCount` is the inclusive upper bound of the
 * monthly rental index this band covers; `null` means "and above" (the last band).
 * Bands are evaluated in order; the first band whose `upToCount` is >= the rental's
 * 1-based monthly index (or whose `upToCount` is null) applies.
 *
 * Example: `[{ upToCount: 50, priceCents: 100 }, { upToCount: null, priceCents: 50 }]`
 * → rentals 1..50 cost 100c each, 51+ cost 50c each (graduated, monthly reset).
 */
export interface PayAsYouGoTier {
  upToCount: number | null;
  priceCents: number;
}

/**
 * Per-store pay-as-you-go pricing configuration. Stored as JSON on the store's
 * subscription row. When fields are omitted, the platform default ladder applies.
 */
export interface PayAsYouGoConfig {
  /**
   * Flat lifetime rate (in cents) per rental that OVERRIDES the tier ladder.
   * Used for exclusive launch offers (e.g. 25c per rental for life).
   * `null`/omitted = use the tier ladder.
   */
  flatRateCents?: number | null;
  /** Graduated tier ladder. When omitted, the platform default ladder applies. */
  tiers?: PayAsYouGoTier[];
  /** ISO 4217 currency for the commission (lowercase, e.g. 'eur'). Default: 'eur'. */
  currency?: string;
}

export interface PlanFeatures {
  // Limits
  maxProducts: number | null; // null = unlimited
  maxReservationsPerMonth: number | null; // null = unlimited
  maxCustomers: number | null; // null = unlimited
  maxCollaborators: number | null; // null = unlimited, 0 = none
  maxSmsPerMonth: number | null; // null = unlimited, 0 = none
  // Monthly INCLUDED AI advisor credits (null = unlimited, 0 = none). Value is
  // injected from env at runtime (getPlans), never a hardcoded commercial number.
  aiCreditsPerMonth: number | null;

  // Features
  customDomain: boolean;
  analytics: boolean;
  emailNotifications: boolean;
  prioritySupport: boolean;
  apiAccess: boolean;
  whiteLabel: boolean;
  onlinePayment: boolean;
  customerPortal: boolean;
  reviewBooster: boolean;
  aiAdvisor: boolean;
  /** AI phone receptionist (inbound voice channel over the same AI agent). */
  aiPhone: boolean;
  phoneSupport: boolean;
  dedicatedManager: boolean;
}

// ============================================================================
// Review Booster Types
// ============================================================================

export interface GoogleReview {
  authorName: string;
  authorPhotoUrl?: string;
  authorPhotoBase64?: string; // Cached base64 encoded photo
  rating: number;
  text: string;
  relativeTimeDescription: string;
  time: number; // Unix timestamp
}

export interface ReviewBoosterTemplate {
  subject?: string;
  emailMessage?: string;
  smsMessage?: string;
}

export interface ReviewBoosterSettings {
  enabled: boolean;
  // Google Place info
  googlePlaceId: string | null;
  googlePlaceName: string | null;
  googlePlaceAddress: string | null;
  googleRating: number | null;
  googleReviewCount: number | null;
  // Feature toggles
  displayReviewsOnStorefront: boolean;
  showReviewPromptInPortal: boolean;
  // Automation settings
  autoSendThankYouEmail: boolean;
  autoSendThankYouSms: boolean;
  emailDelayHours: number;
  smsDelayHours: number;
  // Custom template
  template?: ReviewBoosterTemplate;
}

// ============================================================================
// AI Advisor (storefront customer-facing assistant)
// ============================================================================

/**
 * How the advisor participates in checkout:
 * - 'optional': available in the widget, checkout unchanged
 * - 'recommended': non-blocking suggestion shown at checkout
 * - 'required': checkout is blocked until the advisor validates the conversation
 */
export type AiAdvisorMode = "optional" | "recommended" | "required";

export interface AiAdvisorSettings {
  /** Whether the advisor widget is shown on the storefront */
  enabled: boolean;
  /** How the advisor participates in checkout */
  mode: AiAdvisorMode;
  /**
   * Free-text store-level context read by the advisor — what the owner would
   * tell a new employee (constraints, verifications to run, tone, policies).
   */
  storeContext: string;
  /** First assistant bubble shown when the widget opens (falls back to i18n) */
  welcomeMessage?: string;
  /** Widget title (falls back to i18n) */
  displayName?: string;
}

/**
 * Cart snapshot frozen when the advisor validates a conversation
 * (required mode). Checkout only passes when the reservation matches it —
 * same products, quantities and rental period.
 */
export interface AdvisorValidatedCart {
  items: { productId: string; quantity: number }[];
  startDate: string | null;
  endDate: string | null;
}

// ============================================================================
// AI Phone Receptionist (inbound voice channel)
// ============================================================================

/**
 * When the AI receptionist answers:
 * - 'always': the AI answers every inbound call.
 * - 'after_hours': during the store's business hours the call is transferred
 *   to `transferNumber` (if set), otherwise the AI answers. Outside business
 *   hours the AI always answers. Lets the AI act as an overflow / after-hours
 *   line without changing how the merchant handles calls during opening hours.
 */
export type AiPhoneAnswerMode = "always" | "after_hours";

/**
 * Per-store configuration of the AI phone receptionist. Mirrors
 * `AiAdvisorSettings`: the feature is a NEW voice channel in front of the same
 * AI agent, so the settings that differ from the text advisor live here.
 * The whole feature stays invisible unless the operator configured a voice
 * provider AND the store opted in (see isPhoneReceptionistActiveForStore).
 */
export interface AiPhoneSettings {
  /** Whether the store opted the receptionist in. */
  enabled: boolean;
  /**
   * BCP 47-ish locale the receptionist speaks (e.g. 'fr', 'en'). Independent
   * from the storefront locale — a phone caller has no browser session, so the
   * language is configured, not detected.
   */
  language: string;
  /**
   * Whether the receptionist may create (pending) reservations, or only inform
   * about products, prices and availability. Mirrors the advisor's role split.
   */
  canTakeReservations: boolean;
  /** When the AI answers (see AiPhoneAnswerMode). */
  answerMode: AiPhoneAnswerMode;
  /**
   * Extra spoken sentence appended after the MANDATORY AI-disclosure greeting
   * (EU AI Act art. 50). Optional — a neutral greeting is used when empty.
   */
  greeting?: string;
  /**
   * Human fallback number in E.164 (e.g. '+33123456789') used for
   * 'after_hours' transfer and for the caller's "talk to a human" request.
   * When empty, transfer is unavailable and the AI takes a message instead.
   */
  transferNumber?: string;
  /** Provider-specific TTS voice id/name. Falls back to a provider default. */
  voice?: string;
  /**
   * Opt-in call recording. When true, the caller is told at pickup that the
   * call is recorded (GDPR/CNIL) and the merchant can replay it from the
   * dashboard. Defaults to false — recording is never on without consent.
   */
  recordCalls?: boolean;
}

// ============================================================================
// Notification Settings (Admin notifications)
// ============================================================================

export type NotificationEventType =
  | "reservation_new"
  | "reservation_confirmed"
  | "reservation_rejected"
  | "reservation_cancelled"
  | "reservation_picked_up"
  | "reservation_completed"
  | "reservation_reminder_pickup"
  | "reservation_reminder_return"
  | "payment_received"
  | "payment_failed";

export const NOTIFICATION_EVENT_TYPES: NotificationEventType[] = [
  "reservation_new",
  "reservation_confirmed",
  "reservation_rejected",
  "reservation_cancelled",
  "reservation_picked_up",
  "reservation_completed",
  "reservation_reminder_pickup",
  "reservation_reminder_return",
  "payment_received",
  "payment_failed",
];

export interface NotificationChannelConfig {
  email: boolean;
  sms: boolean;
  discord: boolean;
  push: boolean;
}

export interface NotificationSettings {
  reservation_new: NotificationChannelConfig;
  reservation_confirmed: NotificationChannelConfig;
  reservation_rejected: NotificationChannelConfig;
  reservation_cancelled: NotificationChannelConfig;
  reservation_picked_up: NotificationChannelConfig;
  reservation_completed: NotificationChannelConfig;
  reservation_reminder_pickup: NotificationChannelConfig;
  reservation_reminder_return: NotificationChannelConfig;
  payment_received: NotificationChannelConfig;
  payment_failed: NotificationChannelConfig;

  // Automatic admin reminder timing (independent of customer reminder timing)
  reminderSettings?: {
    // Pickup reminder: hours before startDate to alert the admin (default: 24)
    pickupReminderHours: number;
    // Return reminder: hours before endDate to alert the admin (default: 24)
    returnReminderHours: number;
    // Delivery mode for admin reminders:
    //  - 'per_reservation' (default): one reminder per reservation, sent
    //    `pickup/returnReminderHours` before each event.
    //  - 'daily_digest': one consolidated summary per day listing that day's
    //    pickups and returns, sent at `digestHour` in the store's timezone.
    mode?: AdminReminderMode;
    // Store-local hour (0-23) to send the daily digest. Default: 8 (08:00).
    digestHour?: number;
  };
}

export type AdminReminderMode = "per_reservation" | "daily_digest";

export const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = {
  // Push defaults ON only for new reservations — the #1 real-time event.
  reservation_new: { email: true, sms: false, discord: false, push: true },
  reservation_confirmed: { email: true, sms: false, discord: false, push: false },
  reservation_rejected: { email: true, sms: false, discord: false, push: false },
  reservation_cancelled: { email: true, sms: false, discord: false, push: false },
  reservation_picked_up: { email: false, sms: false, discord: false, push: false },
  reservation_completed: { email: false, sms: false, discord: false, push: false },
  // Admin reminders are opt-in (off by default; SMS/email/Discord cost money or noise)
  reservation_reminder_pickup: { email: false, sms: false, discord: false, push: false },
  reservation_reminder_return: { email: false, sms: false, discord: false, push: false },
  payment_received: { email: true, sms: false, discord: false, push: false },
  payment_failed: { email: true, sms: false, discord: false, push: false },

  // Default admin reminder timing: 24 hours before event, per-reservation mode
  reminderSettings: {
    pickupReminderHours: 24,
    returnReminderHours: 24,
    mode: "per_reservation",
    digestHour: 8,
  },
};

// ============================================================================
// Customer Notification Settings (Notifications sent to customers)
// ============================================================================

export type CustomerNotificationEventType =
  | "customer_request_received"
  | "customer_request_accepted"
  | "customer_request_rejected"
  | "customer_reservation_confirmed"
  | "customer_reminder_pickup"
  | "customer_reminder_return"
  | "customer_payment_requested"
  | "customer_deposit_authorization_requested"
  | "customer_quote_sent"
  | "customer_quote_accepted";

export const CUSTOMER_NOTIFICATION_EVENT_TYPES: CustomerNotificationEventType[] = [
  "customer_request_received",
  "customer_request_accepted",
  "customer_request_rejected",
  "customer_reservation_confirmed",
  "customer_reminder_pickup",
  "customer_reminder_return",
  "customer_payment_requested",
  "customer_deposit_authorization_requested",
  "customer_quote_sent",
  "customer_quote_accepted",
];

export interface CustomerNotificationChannelConfig {
  enabled: boolean;
  email: boolean;
  sms: boolean;
}

export interface CustomerNotificationTemplate {
  subject?: string;
  emailMessage?: string;
  smsMessage?: string;
}

export interface CustomerNotificationSettings {
  // Preferences per event type
  customer_request_received: CustomerNotificationChannelConfig;
  customer_request_accepted: CustomerNotificationChannelConfig;
  customer_request_rejected: CustomerNotificationChannelConfig;
  customer_reservation_confirmed: CustomerNotificationChannelConfig;
  customer_reminder_pickup: CustomerNotificationChannelConfig;
  customer_reminder_return: CustomerNotificationChannelConfig;
  customer_payment_requested: CustomerNotificationChannelConfig;
  customer_deposit_authorization_requested: CustomerNotificationChannelConfig;
  customer_quote_sent: CustomerNotificationChannelConfig;
  customer_quote_accepted: CustomerNotificationChannelConfig;

  // Custom templates
  templates: {
    customer_request_received?: CustomerNotificationTemplate;
    customer_request_accepted?: CustomerNotificationTemplate;
    customer_request_rejected?: CustomerNotificationTemplate;
    customer_reservation_confirmed?: CustomerNotificationTemplate;
    customer_reminder_pickup?: CustomerNotificationTemplate;
    customer_reminder_return?: CustomerNotificationTemplate;
    customer_payment_requested?: CustomerNotificationTemplate;
    customer_deposit_authorization_requested?: CustomerNotificationTemplate;
    customer_quote_sent?: CustomerNotificationTemplate;
    customer_quote_accepted?: CustomerNotificationTemplate;
  };

  // Automatic reminder settings
  reminderSettings?: {
    // Pickup reminder: hours before startDate to send reminder (default: 24)
    pickupReminderHours: number;
    // Return reminder: hours before endDate to send reminder (default: 24)
    returnReminderHours: number;
  };
}

export const DEFAULT_CUSTOMER_NOTIFICATION_SETTINGS: CustomerNotificationSettings = {
  // Reservation journey - email enabled by default
  customer_request_received: { enabled: true, email: true, sms: false },
  customer_request_accepted: { enabled: true, email: true, sms: false },
  customer_request_rejected: { enabled: true, email: true, sms: false },
  customer_reservation_confirmed: { enabled: true, email: true, sms: false },

  // Reminders - email enabled by default (SMS disabled due to cost)
  customer_reminder_pickup: { enabled: true, email: true, sms: false },
  customer_reminder_return: { enabled: true, email: true, sms: false },

  // Payment requests - email enabled by default
  customer_payment_requested: { enabled: true, email: true, sms: false },
  customer_deposit_authorization_requested: {
    enabled: true,
    email: true,
    sms: false,
  },

  // Quotes - email enabled by default
  customer_quote_sent: { enabled: true, email: true, sms: false },
  customer_quote_accepted: { enabled: true, email: true, sms: false },

  // No custom templates by default
  templates: {},

  // Default reminder timing: 24 hours before event
  reminderSettings: {
    pickupReminderHours: 24,
    returnReminderHours: 24,
  },
};
